import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import Fuse from "fuse.js";

import { getBookingsSearchList } from "../../utils/booking";
import { UserContext } from "../../App";
import { getObjSearchList } from "../../utils/search";
import SearchModal from "./SearchModal";

export const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { userInfos } = useContext(UserContext);

  const [data, setData] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const timeoutId = useRef(null);
  const dataLoaded = useRef(false);

  const getData = useCallback(async () => {
    if (dataLoaded.current) return;

    dataLoaded.current = true;

    const bookings = await getBookingsSearchList(userInfos);
    const folders = await getObjSearchList(
      userInfos,
      "folders",
      (val) => val.name
    );
    const cityTarifs = await getObjSearchList(
      userInfos,
      "CityTariffs",
      (val) => val.cityName
    );
    const companies = await getObjSearchList(
      userInfos,
      "company",
      (val) => val.name
    );
    const customers = await getObjSearchList(
      userInfos,
      "customer",
      (val) => val.firstname + " " + val.lastname
    );
    const drivers = await getObjSearchList(
      userInfos,
      "driver",
      (val) => val.name
    );
    const events = await getObjSearchList(
      userInfos,
      "events",
      (val) => val.name
    );
    const providers = await getObjSearchList(
      userInfos,
      "provider",
      (val) => val.name
    );
    const vehicles = await getObjSearchList(
      userInfos,
      "vehicle",
      (val) => val.plateNumber
    );
    const estimates = await getObjSearchList(
      userInfos,
      "estimates",
      (val) =>
        val?.customer?.name ||
        `${val?.customer?.firstname} ${val?.customer?.lastname}`
    );
    const invoices = await getObjSearchList(
      userInfos,
      "invoices",
      (val) =>
        val?.customer?.name ||
        `${val?.customer?.firstname} ${val?.customer?.lastname}`
    );

    const res = [
      ...bookings,
      ...companies,
      ...customers,
      ...drivers,
      ...events,
      ...providers,
      ...vehicles,
      ...estimates,
      ...invoices,
      ...folders,
      ...cityTarifs,
    ];

    setData(res);
  }, [userInfos]);

  const getResults = useCallback(
    async (search) => {
      if (!search || search.length < 3) return;

      if (!dataLoaded.current) {
        await getData();
        return;
      }

      const fuse = new Fuse(data, {
        keys: [
          {
            name: "id",
            weight: 10,
          },
          {
            name: "title",
            weight: 5,
          },
          {
            name: "description",
            weight: 1,
          },
        ],
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: 2,
        ignoreLocation: true,
        shouldSort: true,
        threshold: 0.3,
      });

      let results = fuse.search(search);
      setResults(results);
    },
    [data, getData]
  );

  useEffect(() => {
    if (timeoutId.current) clearTimeout(timeoutId.current);

    if (!searchTerm || searchTerm.length < 3) return;

    setModalOpen(true);
    setLoading(true);

    timeoutId.current = setTimeout(() => {
      getResults(searchTerm).then(() => setLoading(false));
    }, 1000);
    return () => clearTimeout(timeoutId.current);
  }, [getResults, searchTerm]);

  return (
    <SearchContext.Provider value={{ searchTerm, setSearchTerm, setModalOpen }}>
      <SearchModal
        results={results}
        open={modalOpen}
        setOpen={setModalOpen}
        loading={loading}
      />
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};
